import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as catalogApi from '../api/endpoints/catalog';
import { visibleCategories } from '../utils/categories';
import { resolveRailVisual } from '../config/categoryVisuals';
import { focusRing, homeRail } from '../styles/ui';

// The white rail that sits directly under the hero. It renders the *real*
// admin taxonomy from GET /categories — the same source /categories uses — so
// it can never offer a category the catalogue does not have, and the product
// count in each tile is the backend's own eligible count for the browsing
// location. Tiles are spread across the full width in the API's order.
//
// One geometry for every tile, whatever its title length:
//   96px photo disc (88px below xl): white ring, hairline border, soft shadow
//   34px title zone with the centred name; a
//        long name like "Fresh Fruits & Vegetables" wraps inside the zone and
//        never moves the count
//   count on a fixed line under the title zone
// The whole tile is the link: hover tints it and lifts the photo 2px; keyboard focus draws a ring around the tile.
// At xl the tiles share the card width equally; below, the rail scrolls
// sideways with snap points and fixed tile widths.
const CategoryStrip = () => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let active = true;
    catalogApi
      .listCategories()
      .then((response) => {
        if (active) setCategories(visibleCategories(response.data));
      })
      // The homepage must still render if the catalogue call fails; the rail
      // simply collapses rather than showing a broken row.
      .catch(() => {
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
  }, []);

  if (categories.length === 0) return null;

  // The backend's count, or its "nothing here yet" — never a made-up number.
  const countText = (category) => {
    if (typeof category.count !== 'number') return null;
    if (category.count > 0) return `${category.count}+ products`;
    return category.isEmpty ? 'Coming soon' : '0 products';
  };

  return (
    <section aria-label="Shop by category" className="relative z-20 bg-[#F3F9F1]">
      <div className={homeRail}>
        <div className="relative bg-white rounded-[24px] border border-[#E4EEE4] shadow-[0_18px_40px_-26px_rgba(15,51,24,0.32),0_1px_3px_rgba(15,51,24,0.04)] px-2 sm:px-3 xl:px-4 py-3 xl:py-[10px]">
          {/* Below xl the rail scrolls sideways; the fade shows there is more. */}
          <span
            className="xl:hidden pointer-events-none absolute right-0 inset-y-0 w-12 rounded-r-[24px] bg-gradient-to-l from-white to-transparent z-10"
            aria-hidden="true"
          />
          <ul
            className="flex xl:grid xl:grid-flow-col xl:auto-cols-fr overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-px-2 xl:overflow-visible"
            data-testid="category-strip"
          >
            {categories.map((category, index) => {
              const { image, Icon } = resolveRailVisual(category.key, category.iconHint);
              const count = countText(category);
              return (
                <li
                  key={category.key}
                  className={`relative snap-start shrink-0 w-[136px] sm:w-[156px] xl:w-auto ${
                    index > 0
                      ? 'before:absolute before:left-0 before:top-[30px] before:bottom-[30px] before:w-px before:bg-[#EEF3EE]'
                      : ''
                  }`}
                >
                  <Link
                    to={`/categories/${category.key}`}
                    className={`group relative flex flex-col items-center rounded-2xl px-2 pt-1.5 pb-1 cursor-pointer transition-colors duration-200 hover:bg-[#F5FAF3] focus-visible:bg-[#F5FAF3] ${focusRing}`}
                  >
                    <span
                      className={`w-[88px] h-[88px] xl:w-[96px] xl:h-[96px] rounded-full overflow-hidden flex items-center justify-center shrink-0 ring-[3px] ring-white border border-[#E3ECE3] shadow-[0_10px_20px_-12px_rgba(15,51,24,0.5)] transition-transform duration-200 ease-out group-hover:-translate-y-[2px] ${
                        image ? 'bg-[#F4F7F3]' : 'bg-[#E4F3E5]'
                      }`}
                    >
                      {image ? (
                        <img
                          src={image}
                          alt=""
                          width="96"
                          height="96"
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        // No photo for this category — its icon stands in,
                        // on a disc of the same size.
                        <Icon className="w-8 h-8 text-emerald-700" strokeWidth={1.8} aria-hidden="true" />
                      )}
                    </span>

                    {/* Fixed two-line title box: one-line and two-line names
                        share the same centre line, and the count below it
                        stays on the same baseline for every tile. */}
                    <span className="mt-[8px] h-[34px] w-full flex items-center justify-center">
                      <span className="min-w-0 max-w-[120px] text-[14px] font-bold text-emerald-950 text-center leading-[16px] line-clamp-2 transition-colors group-hover:text-emerald-700">
                        {category.label}
                      </span>
                    </span>
                    <span className="mt-[2px] h-[15px] text-[12px] text-[#5E6B63] leading-[15px] text-center whitespace-nowrap">
                      {count}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default CategoryStrip;
